import axios from 'axios';
import { connect } from 'react-redux';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { AccessTime, Add, Check, Close, EditOutlined, KeyboardArrowDown } from '@mui/icons-material';
import { Menu, MenuItem, Modal } from '@mui/material';
import { validTokenExists } from '../firebaseHelper';
import GroupedNotes from './GroupedNotes';
import Loading from './Loading';
import SigninButtonPopup from './SigninButtonPopup';
import NotesTranscriptPreview from './NotesTranscriptPreview';
import NoteTimestampEditor from './NoteTimestampEditor';
import { getDisplaySpeakerData } from './speakerHelper';
import { countNoteCharacters, limitNoteText } from './noteTextHelper';
import { fancyTimeFormat } from './timeHelper';
import './Notes.css';
import './NoteMessage.css';

const MaxNoteCharacters = 200;
const QuickEmoji = ['🚀', '❤️', '⭐', '🛒', '📚', '😂', '😢', '🤯'];
const SigninButtonPopupForwardRef = forwardRef((props, ref) => <SigninButtonPopup {...props} />);
const messageText = message => (message?.message || []).map(word => word.text).join('');
const notesShareContext = (left, right) => {
    if (!left?.trackId || left.trackId !== right.trackId) return false;
    if (left.messageId || right.messageId) return !!left.messageId && left.messageId === right.messageId;
    // The notes API groups timestamp-only notes by whole second. A missing
    // timestamp must not be mistaken for an explicit position at zero.
    return Number.isFinite(left.timeSeconds) && Number.isFinite(right.timeSeconds) &&
        Math.floor(left.timeSeconds) === Math.floor(right.timeSeconds);
};

const playbackPosition = (trackId, track) => {
    const audio = document.getElementById('audio');
    return track?.id === trackId && audio && track.mp3URL && audio.src === track.mp3URL &&
        Number.isFinite(audio.currentTime) ? Math.max(0, audio.currentTime) : null;
};

const initialAttachment = ({ message, speaker, timestamp, trackId, track, manual = false }) => {
    if (message && (!message.trackId || message.trackId === trackId)) {
        return { message, speaker: getDisplaySpeakerData(message.speakerId,
            speaker ? [speaker] : track?.speakers, message.anonymousDisplay) };
    }
    if (Number.isFinite(timestamp) && timestamp >= 0) return { timestamp };
    const currentTime = playbackPosition(trackId, track);
    if (track?.id !== trackId || (currentTime === null && !manual)) return null;
    const currentMessage = (currentTime === null ? null
        : [...(track.transcript || [])].reverse().find(item => item.timeSeconds <= currentTime))
        || (manual ? track.transcript?.[0] : null);
    return currentMessage ? {
        message: currentMessage,
        speaker: getDisplaySpeakerData(currentMessage.speakerId, track.speakers, currentMessage.anonymousDisplay),
    } : currentTime !== null ? { timestamp: currentTime } : null;
};

const NoteMessage = ({ open, message, speaker, timestamp, exit, trackId, track, uid, authenticated }) => {
    const textareaRef = useRef(null);
    const initializedTrack = useRef(null);
    const draftVersion = useRef(0);
    const attachmentInitialized = useRef(false);
    const loadMoreRef = useRef(null);
    const requestVersion = useRef(0);
    const replacementFeedVersion = useRef(0);
    const loadingRequest = useRef(false);
    const lastNoteId = useRef(null);
    const pendingVotes = useRef(new Set());
    const postingRequest = useRef(false);
    const latestFetchNotes = useRef(null);
    const [noteText, setNoteText] = useState('');
    const [expanded, setExpanded] = useState(false);
    const [attachment, setAttachment] = useState(null);
    const [editingTimestamp, setEditingTimestamp] = useState(false);
    const [sortByTop, setSortByTop] = useState(true);
    const [sortAnchor, setSortAnchor] = useState(null);
    const [groupedNotes, setGroupedNotes] = useState([]);
    const [votes, setVotes] = useState({});
    const [canLoadMore, setCanLoadMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [addingNote, setAddingNote] = useState(false);
    const [openSigninButtonPopup, setOpenSigninButtonPopup] = useState(false);
    const characterCount = countNoteCharacters(noteText);
    const canAttachTranscript = track?.id === trackId && !!track.transcript?.length;
    const canAttachTimestamp = playbackPosition(trackId, track) !== null;

    useEffect(() => {
        if (!open || initializedTrack.current === trackId) return;
        initializedTrack.current = trackId;
        draftVersion.current += 1;
        setNoteText('');
        setEditingTimestamp(false);
        attachmentInitialized.current = !!message || Number.isFinite(timestamp);
        setAttachment(attachmentInitialized.current ? initialAttachment({ message, speaker, timestamp, trackId, track }) : null);
        setExpanded(!!message);
        setActionError(null);
    }, [open, trackId, message, speaker, timestamp, track]);

    useEffect(() => {
        if (expanded) textareaRef.current?.focus();
    }, [expanded]);

    useEffect(() => {
        if (!open) setEditingTimestamp(false);
    }, [open]);

    useEffect(() => () => {
        // Late writes may finish on the server, but cannot update a departed drawer.
        draftVersion.current += 1;
    }, [open, trackId, uid, authenticated]);

    const fetchNotes = useCallback(async (reset = false, requestedVersion) => {
        if (loadingRequest.current && !reset) return;
        const version = requestedVersion ?? ++requestVersion.current;
        loadingRequest.current = true;
        setLoading(true);
        setLoadError(null);
        const body = { trackId, isPrivate: false };
        if (sortByTop) body.sortBy = 'top';
        if (!reset && lastNoteId.current) body.lastNoteId = lastNoteId.current;
        try {
            const signedIn = await validTokenExists().then(() => true, () => false);
            if (version !== requestVersion.current) return;
            const { data } = await axios.post(signedIn ? '/notes_track' : '/track_notes', body);
            if (version !== requestVersion.current) return;
            if (data.error) throw new Error(data.error);
            const groups = data.groupedNotes || [];
            const nextVotes = Object.fromEntries((data.votes || []).map(vote => [vote.noteId, vote]));
            if (reset) replacementFeedVersion.current = version;
            setGroupedNotes(previous => {
                const retained = reset ? [] : previous;
                const ids = new Set(retained.flatMap(group => group.notes.map(note => note.id)));
                return [...retained, ...groups.map(group => ({ ...group,
                    notes: group.notes.filter(note => !ids.has(note.id) && ids.add(note.id)),
                })).filter(group => group.notes.length > 0)];
            });
            setVotes(previous => reset ? nextVotes : { ...previous, ...nextVotes });
            setCanLoadMore(Boolean(data.lastNoteId) && data.lastNoteId !== body.lastNoteId);
            lastNoteId.current = data.lastNoteId || null;
        } catch (error) {
            if (version === requestVersion.current) {
                if (body.lastNoteId && error.response?.status === 403) lastNoteId.current = null;
                setLoadError('Couldn’t load notes. Please try again.');
            }
        } finally {
            if (version === requestVersion.current) {
                loadingRequest.current = false;
                setLoading(false);
            }
        }
    }, [trackId, sortByTop]);
    latestFetchNotes.current = fetchNotes;

    useEffect(() => {
        const version = ++requestVersion.current;
        loadingRequest.current = false;
        lastNoteId.current = null;
        setGroupedNotes([]);
        setVotes({});
        setCanLoadMore(false);
        if (open) fetchNotes(true, version);
        return () => { requestVersion.current += 1; };
    }, [open, fetchNotes, uid, authenticated]);

    useEffect(() => {
        if (!open || !canLoadMore || loading || loadError || !loadMoreRef.current ||
            typeof IntersectionObserver === 'undefined') return;
        let active = true;
        const observer = new IntersectionObserver(entries => {
            if (!active || !entries.some(entry => entry.isIntersecting)) return;
            active = false;
            observer.disconnect();
            fetchNotes();
        });
        observer.observe(loadMoreRef.current);
        return () => { active = false; observer.disconnect(); };
    }, [open, canLoadMore, loading, loadError, groupedNotes, fetchNotes]);

    const refreshAfterMutation = (feedVersion, deletedCursor = false) => {
        if (!deletedCursor && !loadingRequest.current && feedVersion === requestVersion.current) return;
        // A feed snapshot requested before this write may omit a new note or
        // restore a deleted one. Replace it with a fresh first page.
        lastNoteId.current = null;
        setCanLoadMore(false);
        latestFetchNotes.current(true, ++requestVersion.current);
    };

    const handleSubmit = async event => {
        event.preventDefault();
        const text = noteText.trim();
        if (!text || postingRequest.current) return;
        postingRequest.current = true;
        setAddingNote(true);
        setActionError(null);
        const version = draftVersion.current;
        const feedVersion = requestVersion.current;
        try {
            try { await validTokenExists(); } catch {
                if (version === draftVersion.current) setOpenSigninButtonPopup(true);
                return;
            }
            if (version !== draftVersion.current) return;
            const body = { text, trackId, isPrivate: false };
            if (attachment?.message?.id) body.messageId = attachment.message.id;
            else if (Number.isFinite(attachment?.timestamp)) body.timeSeconds = attachment.timestamp;
            const { data } = await axios.post('/note_add', body);
            if (version !== draftVersion.current) return;
            if (data.error || !data.note) throw new Error(data.error || 'Missing note');
            if (data.vote) setVotes(previous => ({ ...previous, [data.vote.noteId]: data.vote }));
            setGroupedNotes(previous => {
                const groupIndex = previous.findIndex(group => notesShareContext(group.notes[0], data.note));
                if (groupIndex < 0) return [{ notes: [data.note] }, ...previous];
                const group = previous[groupIndex];
                return [{ ...group, notes: [data.note, ...group.notes.filter(note => note.id !== data.note.id)] },
                    ...previous.filter((_, index) => index !== groupIndex)];
            });
            setNoteText('');
            setAttachment(null);
            attachmentInitialized.current = false;
            setExpanded(false);
            refreshAfterMutation(feedVersion);
        } catch {
            if (version === draftVersion.current) setActionError('Couldn’t post your note. Your draft is saved here. Try again.');
        } finally {
            postingRequest.current = false;
            setAddingNote(false);
        }
    };

    const deleteNote = noteId => async () => {
        const version = draftVersion.current;
        const feedVersion = requestVersion.current;
        try {
            await validTokenExists();
            if (version !== draftVersion.current) return;
            const { data } = await axios.post('/note_delete', { noteId });
            if (version !== draftVersion.current) return;
            if (data.error) throw new Error(data.error);
            setGroupedNotes(previous => previous.map(group => ({
                ...group, notes: group.notes.filter(note => note.id !== noteId),
            })).filter(group => group.notes.length));
            setActionError(null);
            refreshAfterMutation(feedVersion, noteId === lastNoteId.current);
        } catch {
            if (version === draftVersion.current) setActionError('Couldn’t delete the note. Please try again.');
        }
    };

    const updateVote = async (noteId, vote) => {
        if (pendingVotes.current.has(noteId)) return;
        pendingVotes.current.add(noteId);
        const version = draftVersion.current;
        const feedVersion = requestVersion.current;
        try {
            try { await validTokenExists(); } catch {
                if (version === draftVersion.current) setOpenSigninButtonPopup(true);
                return;
            }
            if (version !== draftVersion.current) return;
            const voteFeedVersion = replacementFeedVersion.current;
            const oldVote = votes[noteId];
            const newStatus = oldVote?.voteStatus === vote ? null : vote;
            const upDelta = Number(newStatus === true) - Number(oldVote?.voteStatus === true);
            const downDelta = Number(newStatus === false) - Number(oldVote?.voteStatus === false);
            const changeCounts = direction => setGroupedNotes(previous => previous.map(group => ({
                ...group, notes: group.notes.map(note => note.id === noteId ? {
                    ...note, upVotes: (note.upVotes || 0) + direction * upDelta,
                    downVotes: (note.downVotes || 0) + direction * downDelta,
                } : note),
            })));
            setVotes(previous => ({ ...previous, [noteId]: { noteId, voteStatus: newStatus } }));
            changeCounts(1);
            try {
                const { data } = await axios.post('/note_vote', { noteId, voteStatus: newStatus });
                if (version !== draftVersion.current) return;
                if (data.error) throw new Error(data.error);
                refreshAfterMutation(feedVersion);
            } catch {
                if (version === draftVersion.current) {
                    // Only undo counts in the snapshot we optimistically changed.
                    // A newer feed already contains server-provided counts.
                    if (voteFeedVersion === replacementFeedVersion.current) {
                        setVotes(previous => ({ ...previous, [noteId]: oldVote }));
                        changeCounts(-1);
                    }
                    refreshAfterMutation(feedVersion);
                    setActionError('Couldn’t update your vote. Please try again.');
                }
            }
        } finally {
            pendingVotes.current.delete(noteId);
        }
    };

    const expandComposer = () => {
        if (!attachmentInitialized.current) {
            setAttachment(initialAttachment({ trackId, track }));
            attachmentInitialized.current = true;
        }
        setExpanded(true);
        setActionError(null);
    };

    return (
        <div className='appScreen notesEpisodeScreen'>
            <Modal open={openSigninButtonPopup} onClose={() => setOpenSigninButtonPopup(false)}>
                <SigninButtonPopupForwardRef close={() => setOpenSigninButtonPopup(false)} />
            </Modal>
            {open && editingTimestamp && Number.isFinite(attachment?.timestamp) && <NoteTimestampEditor
                key={trackId} initialSeconds={attachment.timestamp}
                durationSeconds={track?.id === trackId ? track.totalTimeSeconds : undefined}
                audioSource={track?.id === trackId ? track.mp3URL : undefined}
                onClose={() => setEditingTimestamp(false)}
                onSave={value => { setAttachment({ timestamp: value }); setEditingTimestamp(false); }} />}
            <div className='container appDrawerSurface notesEpisodeSurface'>
                <div className='horizontalCenter'>
                    <div className='fixedDrawerHeader'>
                        <div className='notesEpisodeHeader'>
                            <div className='heading'>Notes</div>
                            <button type='button' aria-label='Close notes' onClick={exit}><Close /></button>
                        </div>
                    </div>
                    <div className='hasFixedDrawerHeader notesEpisodeContent'>
                        {groupedNotes.length > 0 && <div className='notesEpisodeSort'>
                            <button type='button' aria-label={`Sort notes: ${sortByTop ? 'Top' : 'Latest'}`}
                                aria-haspopup='menu' aria-expanded={!!sortAnchor}
                                onClick={event => setSortAnchor(event.currentTarget)}>
                                {sortByTop ? 'Top' : 'Latest'}<KeyboardArrowDown />
                            </button>
                            <Menu anchorEl={sortAnchor} open={!!sortAnchor} onClose={() => setSortAnchor(null)}
                                PaperProps={{ className: 'notesPopup' }}>
                                {[{ label: 'Top', value: true }, { label: 'Latest', value: false }].map(option => (
                                    <MenuItem key={option.label} selected={sortByTop === option.value}
                                        onClick={() => { setSortByTop(option.value); setSortAnchor(null); }}>
                                        {option.label}{sortByTop === option.value && <Check fontSize='small' />}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </div>}
                        {loadError && <div className='notesEpisodeError' role='alert'>
                            <span>{loadError}</span><button type='button' onClick={() => fetchNotes(!lastNoteId.current)}>Retry</button>
                        </div>}
                        {loading && !groupedNotes.length ? <Loading overrideStyle={{ padding: '40px', textAlign: 'center' }} />
                            : !loadError && !groupedNotes.length ? <div className='notesEpisodeEmpty'>
                                <div>No notes yet</div><p>Add a note while you listen.</p>
                            </div> : groupedNotes.map(group => <div className='notesEpisodeGroup' key={group.id || group.notes[0]?.id}>
                                <GroupedNotes group={group} votes={votes} deleteNote={deleteNote}
                                    voteUp={noteId => () => updateVote(noteId, true)}
                                    voteDown={noteId => () => updateVote(noteId, false)} />
                            </div>)}
                        {canLoadMore && !loadError && <div className='notesEpisodeLoadMore' ref={loadMoreRef}>
                            <button type='button' disabled={loading} onClick={() => fetchNotes()}>
                                {loading ? 'Loading more…' : 'Load more'}
                            </button>
                        </div>}
                    </div>
                    {expanded && <button type='button' className='notesComposerBackdrop' aria-label='Close note editor'
                        onClick={() => setExpanded(false)} />}
                    <div className={`notesComposerDock${expanded ? ' notesComposerDockExpanded' : ''}`}>
                        {!expanded ? <button type='button' className='notesComposerCollapsed' onClick={expandComposer}>
                            <EditOutlined fontSize='small' /><span>{noteText || 'Write a note…'}</span>
                        </button> : <form onSubmit={handleSubmit} className='notesComposerForm'>
                            {attachment?.message && <NotesTranscriptPreview
                                messageId={attachment.message.id} messageText={messageText(attachment.message)}
                                speaker={attachment.speaker} lines={2}
                                onChange={addingNote ? undefined : (newMessage, newSpeaker) => setAttachment({ message: newMessage, speaker: newSpeaker })}
                                onRemove={addingNote ? undefined : () => setAttachment(null)} />}
                            {!attachment?.message && Number.isFinite(attachment?.timestamp) && <div className='notesComposerTimestamp'>
                                <button type='button' className='notesComposerTimestampEdit' aria-label='Edit note time'
                                    disabled={addingNote} onClick={() => setEditingTimestamp(true)}>
                                    <AccessTime aria-hidden='true' /><span>{fancyTimeFormat(attachment.timestamp)}</span><EditOutlined aria-hidden='true' />
                                </button>
                                <button type='button' aria-label='Remove timestamp' disabled={addingNote} onClick={() => setAttachment(null)}><Close /></button>
                            </div>}
                            {!attachment && (canAttachTranscript || canAttachTimestamp) && <button type='button'
                                className='notesComposerAttach' disabled={addingNote}
                                onClick={() => setAttachment(initialAttachment({ trackId, track, manual: true }))}>
                                <Add aria-hidden='true' />Add {canAttachTranscript ? 'transcript' : 'timestamp'}
                            </button>}
                            <div className='notesComposerInputBubble'>
                                <textarea ref={textareaRef} aria-label='Write a note' placeholder='Write a note…'
                                    rows={3} value={noteText} disabled={addingNote}
                                    onChange={event => setNoteText(limitNoteText(event.target.value, MaxNoteCharacters))}
                                    onKeyDown={event => { if (event.key === 'Escape') { event.preventDefault(); setExpanded(false); } }} />
                                <div className='notesComposerInputActions'>
                                    <span className={`notesComposerCount${characterCount === MaxNoteCharacters ? ' notesComposerCountLimit' : ''}`} aria-live='polite'>
                                        {characterCount >= 180 ? `${characterCount}/${MaxNoteCharacters}` : ''}
                                    </span>
                                    <button type='submit' disabled={!noteText.trim() || addingNote}>{addingNote ? 'Posting…' : 'Post'}</button>
                                </div>
                            </div>
                            <div className='notesComposerEmojiRow'>
                                {QuickEmoji.map(emoji => <button type='button' key={emoji} disabled={addingNote}
                                    aria-label={`Add ${emoji}`} onClick={() => {
                                        setNoteText(previous => limitNoteText(previous + emoji, MaxNoteCharacters));
                                        textareaRef.current?.focus();
                                    }}>{emoji}</button>)}
                            </div>
                        </form>}
                        {actionError && <div className='notesEpisodeError' role='alert'>{actionError}</div>}
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = state => ({ track: state.data.track, uid: state.user.uid, authenticated: state.user.authenticated });
export default connect(mapStateToProps)(NoteMessage);
